// ============================================================================
// components/block/liveStreamManagment/VuMeter.tsx
//
// v1.1 — VU-mètres autonomes, hors du cycle de rendu React.
//
// Ces composants s'abonnent à `audioLevelStore` et écrivent directement dans le
// DOM à chaque frame. Ils ne détiennent aucun state et ne re-rendent jamais en
// réponse à un niveau : seul le changement de `sourceId` provoque un rendu.
//
// C'est ce qui permet à DeckV2, ControlBar et LiveStreamManager de ne plus se
// re-rendre à 60 fps dès qu'un son passe.
//
// v1.1 (2026-10-23) : zones de couleur d'une table de mixage (vuMeterZones), fixes
// sur l'échelle — la barre les découvre au lieu de changer de couleur d'un bloc.
// ============================================================================

import React, { useRef } from 'react';
import { useAudioLevelEffect } from '../../../hooks/liveStreamManagment/useAudioLevel';
import { useVuMeterMode, vuGradient, vuSegments } from './vuMeterZones';

// Échelle des niveaux du store : 0–100 % = −60 → +6 dB (ou LUFS en mode LUFS),
// linéaire (cf. dbToPct dans useAudioMetering).
const DB_FLOOR = -60;
const DB_CEIL  =   6;
const dbToPct  = (db: number) => ((db - DB_FLOOR) / (DB_CEIL - DB_FLOOR)) * 100;

const BAR_GRADIENT = { peak: vuGradient('to right', dbToPct, 'peak'), lufs: vuGradient('to right', dbToPct, 'lufs') };
const BAR_SEGMENTS = vuSegments('to right', 3);

// ── VU compact du deck ────────────────────────────────────────────────────────

/**
 * Paire de barres L/R des boutons du deck. Les deux barres affichent la moyenne
 * des canaux — comportement conservé à l'identique de l'ancienne prop
 * `audioLevel`, qui était déjà la moyenne.
 */
export const SlotVuMeter: React.FC<{ sourceId: string }> = ({ sourceId }) => {
  const barLRef = useRef<HTMLDivElement>(null);
  const barRRef = useRef<HTMLDivElement>(null);
  const mode    = useVuMeterMode();

  useAudioLevelEffect(sourceId, ({ avg }) => {
    const clip = `inset(0 ${100 - avg}% 0 0)`;
    const barL = barLRef.current;
    const barR = barRRef.current;
    if (barL) barL.style.clipPath = clip;
    if (barR) barR.style.clipPath = clip;
  });

  return (
    // gap 3 (et non 1) : la place libérée par le retrait du slider de volume dans les
    // boutons de source du deck permet de dissocier visuellement les voies L et R.
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {(['L', 'R'] as const).map(ch => (
        <div key={ch} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <span style={{ fontSize: 7, color: '#fff', lineHeight: 1, width: 7, textAlign: 'right' }}>{ch}</span>
          <div style={{ position: 'relative', flex: 1, height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 1.5, overflow: 'hidden' }}>
            <div
              ref={ch === 'L' ? barLRef : barRRef}
              style={{ position: 'absolute', inset: 0, background: BAR_GRADIENT[mode], clipPath: 'inset(0 100% 0 0)', transition: 'clip-path 30ms' }}
            />
            <div style={{ position: 'absolute', inset: 0, background: BAR_SEGMENTS, pointerEvents: 'none' }} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default SlotVuMeter;
